import { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import {
  selectPermissionGroupDetail,
  useAlertMessage,
  useNotification,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  addGroupRoleMapping,
  ErrorMessage,
  FeaturePage,
  fetchGroupRoleAvailable,
  fetchGroupRoleComposite,
  fetchGroupRoleMapping,
  QUERY_KEY,
  updateGroupRoleMapping,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  mapRoleToSelect,
  ProtectedRouter,
  RoleMapper,
  SelectComboItem,
} from '@bom-nextgen-keycloak/web/shared/ui';
import { useGroupDetail } from '../../hooks/useGroupDetail';
import { PARENT_PATH } from '../../shared';

export type MutationPayload = {
  clientId: string;
  groupId: string;
  payload: {
    roles: SelectComboItem[];
  };
};

const RoleMapping: FC = () => {
  const { id = '' } = useParams();
  const resource = useResource();
  const { clientId } = resource;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAlertMessage } = useAlertMessage();
  const { setNotification } = useNotification();
  const { groupDetail, isSubscription } = useGroupDetail(id);
  const { isEditRoleMappingTab: canEdit } = useSelector(
    selectPermissionGroupDetail
  );
  const [selected, setSelected] = useState<SelectComboItem[]>([]);

  const groupLevel = groupDetail.level;
  const defaultData = {
    roleAvailable: [],
    roleComposite: [],
    roleMapping: [],
  };
  const queryKeys = [QUERY_KEY.GROUP_ROLE_MAPPING, id];

  const { data = defaultData, isLoading } = useQuery(
    queryKeys,
    () =>
      Promise.all([
        fetchGroupRoleAvailable(id, clientId),
        fetchGroupRoleComposite(id, clientId),
        fetchGroupRoleMapping(id, clientId),
      ]).then(([roleAvailable, roleComposite, roleMapping]) => ({
        roleAvailable: mapRoleToSelect(roleAvailable) || [],
        roleComposite: mapRoleToSelect(roleComposite) || [],
        roleMapping: mapRoleToSelect(roleMapping) || [],
      })),
    {
      onError: (error: AxiosError<ErrorMessage>) => {
        const message = error.response?.data.message || 'Cannot get group list';

        setAlertMessage({
          message,
          statusCode: error.response?.status,
          typeStatusMessage: 'error',
        });
      },
    }
  );

  const addMutation = useMutation(addGroupRoleMapping, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message = error.response?.data.message || 'Cannot add role';

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSettled: async () => {
      handleDeselect();
    },
    onSuccess: () => {
      setNotification({
        message: 'Added roles',
        type: 'success',
      });
      handleReload();
    },
  });

  const updateMutation = useMutation(updateGroupRoleMapping, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message =
        error.response?.data.message || 'Cannot update role mappings';

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSettled: async () => {
      handleDeselect();
    },
    onSuccess: () => {
      setNotification({
        message: 'Removed roles',
        type: 'success',
      });
      handleReload();
    },
  });

  const generateMutationPayload = (
    value: SelectComboItem[]
  ): MutationPayload => {
    const payload = {
      clientId,
      groupId: id,
      payload: { roles: value },
    };

    return payload;
  };

  const handleReload = () => {
    queryClient.invalidateQueries(queryKeys);
  };

  const handleSelect = (value: SelectComboItem[]) => {
    setSelected(value);
  };

  const handleDeselect = () => {
    setSelected([]);
  };

  const handleAdd = (value: SelectComboItem[]) => {
    addMutation.mutate(generateMutationPayload(value));
  };

  const handleRemove = (value: SelectComboItem[]) => {
    updateMutation.mutate(generateMutationPayload(value));
  };

  useEffect(() => {
    if (isSubscription && groupLevel !== 4) {
      navigate(`${PARENT_PATH}/${id}`);
    }
  }, [groupLevel, id, isSubscription, navigate]);

  const { roleAvailable, roleComposite, roleMapping } = data;
  const isProcessing =
    isLoading || addMutation.isLoading || updateMutation.isLoading;

  return (
    <RoleMapper
      canEdit={canEdit}
      loading={isProcessing}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onSelect={handleSelect}
      roleAvailable={roleAvailable}
      roleComposite={roleComposite}
      roleMapping={roleMapping}
      selected={selected}
    />
  );
};

const WrappedRoleMappingPage: FC = () => {
  const { canViewRoleMappingTab } = useSelector(selectPermissionGroupDetail);

  return (
    <ProtectedRouter
      canView={canViewRoleMappingTab}
      feature={FeaturePage.GROUP}
      isEditPage
      navigatePath={PARENT_PATH}
    >
      <RoleMapping />
    </ProtectedRouter>
  );
};

export { WrappedRoleMappingPage };
